import * as React from 'react'
import { createPortal } from 'react-dom'
import { isMobile } from 'react-device-detect'

import { cn } from '@/lib/utils'
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover'
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip'

type CardProps = React.ComponentProps<'div'> & {
  tooltipContent?: React.ReactNode
  tooltipClassName?: string
  /**
   * Opt-in tooltip style used by the infringement detail view only:
   * - desktop: cursor-following tooltip rendered via a portal
   * - mobile:  tap-to-open Popover
   *
   * When omitted (the default), the Card renders the original Radix Tooltip
   * — anchored, hover-delayed, keyboard- and screen-reader-friendly. All
   * existing Gantt consumers (salary, timesheets) rely on that default and
   * must not see a behavior change, so new tooltip variants are added as
   * opt-ins rather than by replacing the default.
   */
  cursorFollow?: boolean
}

function Card({
  className,
  tooltipContent,
  tooltipClassName,
  cursorFollow,
  ...props
}: CardProps) {
  const card = (
    <div
      data-slot="card"
      className={cn(
        'bg-card text-card-foreground flex flex-col gap-6 border py-6',
        className
      )}
      {...props}
    />
  )

  if (!tooltipContent) {
    return card
  }

  if (!cursorFollow) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>{card}</TooltipTrigger>
        <TooltipContent className={tooltipClassName}>
          {tooltipContent}
        </TooltipContent>
      </Tooltip>
    )
  }

  return (
    <CursorFollowCard
      tooltipContent={tooltipContent}
      tooltipClassName={tooltipClassName}
    >
      {card}
    </CursorFollowCard>
  )
}

type CursorFollowCardProps = {
  tooltipContent: React.ReactNode
  tooltipClassName?: string
  children: React.ReactNode
}

/**
 * Cursor-following tooltip variant, split out to keep the hook state scoped
 * to cases that actually use it (avoids per-render state churn for the
 * default anchored-tooltip path used across salary/timesheets).
 */
function CursorFollowCard({
  tooltipContent,
  tooltipClassName,
  children,
}: CursorFollowCardProps) {
  const [mobileOpen, setMobileOpen] = React.useState(false)
  const [desktopOpen, setDesktopOpen] = React.useState(false)
  const [desktopPosition, setDesktopPosition] = React.useState({ x: 0, y: 0 })
  // Keyboard parity: mouse users get a cursor-anchored tooltip; keyboard
  // users get the same tooltip content anchored to the element's center on
  // focus. Without the onFocus/onBlur + tabIndex pair this path silently
  // locks keyboard users out of all bar explanations — the default Radix
  // path has built-in focus handling and we should not regress below that.
  //
  // These hooks are declared before the `isMobile` early return so the
  // hook call order is stable across renders (rules-of-hooks) — harmless
  // on mobile where the desktop branch below is never taken.
  const wrapperRef = React.useRef<HTMLDivElement>(null)
  const anchorFromElement = React.useCallback(() => {
    const el = wrapperRef.current
    if (!el) return
    const rect = el.getBoundingClientRect()
    setDesktopPosition({
      x: rect.left + rect.width / 2,
      y: rect.top + rect.height / 2,
    })
  }, [])

  if (isMobile) {
    return (
      <Popover onOpenChange={setMobileOpen} open={mobileOpen}>
        <PopoverTrigger asChild>{children}</PopoverTrigger>
        <PopoverContent
          align="center"
          className={cn(
            'w-fit max-w-60 rounded-md border-0 bg-foreground px-3 py-1.5 text-center text-sm text-wrap text-background shadow-lg',
            tooltipClassName
          )}
          collisionPadding={12}
          side="top"
          sideOffset={10}
        >
          {tooltipContent}
        </PopoverContent>
      </Popover>
    )
  }

  const tooltip =
    desktopOpen &&
    typeof document !== 'undefined' &&
    createPortal(
      <div
        className={cn(
          'pointer-events-none fixed z-[9999] w-fit max-w-60 rounded-md bg-foreground px-3 py-1.5 text-center text-sm text-wrap text-background shadow-lg',
          tooltipClassName
        )}
        style={{
          left: desktopPosition.x,
          top: desktopPosition.y,
          transform: 'translate(-50%, -100%) translateY(-12px)',
        }}
      >
        {tooltipContent}
      </div>,
      document.body
    )

  return (
    <>
      <div
        ref={wrapperRef}
        className="h-full w-full focus-visible:outline-ring focus-visible:outline-2"
        tabIndex={0}
        onBlur={() => setDesktopOpen(false)}
        onFocus={() => {
          anchorFromElement()
          setDesktopOpen(true)
        }}
        onMouseEnter={event => {
          setDesktopOpen(true)
          setDesktopPosition({ x: event.clientX, y: event.clientY })
        }}
        onMouseLeave={() => setDesktopOpen(false)}
        onMouseMove={event => {
          setDesktopPosition({ x: event.clientX, y: event.clientY })
        }}
      >
        {children}
      </div>
      {tooltip}
    </>
  )
}

export { Card }
